import { useEffect, useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Login from "./Login";
import RecordsPage from "./RecordsPage";
import { ShellRoute } from "@/App";
import { Button } from "@/components/ui/button";
import { Building2, ShieldAlert, LogOut } from "lucide-react";

/**
 * Path-based tenant routing entry point.
 *
 * URL model:  https://ayobell.com/{orgSlug}
 *
 * Behaviour:
 *   1) Probe /api/tenants/by-slug/{orgSlug} (public, no auth).
 *   2) If the slug does NOT match any known active/trial tenant, fall
 *      back to the previous /:moduleKey behaviour so existing generic
 *      module URLs keep working exactly as before (no regression).
 *   3) If it IS a known tenant:
 *        - Unauthenticated user → render the branded Login screen with
 *          a "Signing into: {tenant.name}" banner.
 *        - Authenticated user whose org.slug matches → redirect home
 *          ("/"), where existing server-side org_id enforcement takes
 *          over.
 *        - Authenticated user whose org.slug does NOT match → show a
 *          mismatch banner offering sign-out; DO NOT switch tenants,
 *          DO NOT expose the other tenant's data. Tenant scoping is
 *          already enforced by org_id on the server side, so the
 *          user's existing JWT continues to see only their own tenant.
 *
 * The slug is purely for tenant discovery/UX. Authorisation always
 * comes from the authenticated user's org_id server-side.
 */
export default function TenantEntry() {
  const { orgSlug } = useParams();
  const { user, loading: authLoading, logout } = useAuth();
  const [state, setState] = useState({ loading: true, tenant: null });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/tenants/by-slug/${encodeURIComponent(orgSlug)}`;
        const res = await fetch(url, { credentials: "omit" });
        if (!res.ok) {
          if (!cancelled) setState({ loading: false, tenant: null });
          return;
        }
        const tenant = await res.json();
        if (!cancelled) setState({ loading: false, tenant });
      } catch {
        if (!cancelled) setState({ loading: false, tenant: null });
      }
    })();
    return () => { cancelled = true; };
  }, [orgSlug]);

  if (state.loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300">
        <div className="text-sm animate-pulse">Loading…</div>
      </div>
    );
  }

  // Not a known tenant → preserve legacy /:moduleKey behaviour.
  if (!state.tenant) {
    return (
      <ShellRoute>
        <RecordsPage />
      </ShellRoute>
    );
  }

  const tenant = state.tenant;

  // Authenticated flow — check org match strictly server-issued.
  if (user) {
    const mySlug = user?.org?.slug || user?.org_slug || null;
    if (mySlug && mySlug.toLowerCase() === tenant.slug.toLowerCase()) {
      return <Navigate to="/" replace />;
    }
    // Mismatch — do NOT switch tenants. Offer sign-out.
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div
          data-testid="tenant-mismatch-banner"
          className="max-w-md w-full rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8 text-slate-100 shadow-2xl"
        >
          <div className="flex items-center gap-3 mb-4">
            <ShieldAlert className="h-6 w-6 text-amber-400" />
            <h1 className="text-xl font-semibold">Signed into a different organisation</h1>
          </div>
          <p className="text-sm text-slate-300 mb-2">
            You are currently signed in as <span className="font-medium">{user.email}</span>
            {mySlug ? <> under <span className="font-medium">{mySlug}</span></> : null}.
          </p>
          <p className="text-sm text-slate-300 mb-6">
            To access <span className="font-medium">{tenant.name}</span> please sign out first
            and sign in with a {tenant.name} account.
          </p>
          <div className="flex items-center gap-2">
            <Button
              data-testid="tenant-mismatch-signout-btn"
              onClick={async () => { await logout?.(); navigate(`/${tenant.slug}`, { replace: true }); }}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out and continue to {tenant.name}
            </Button>
            <Button
              data-testid="tenant-mismatch-home-btn"
              variant="outline"
              onClick={() => navigate("/", { replace: true })}
            >
              Back to my dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Public → branded login with tenant hint.
  try { sessionStorage.setItem("ayb.tenantHint", JSON.stringify(tenant)); } catch {}
  return (
    <>
      <div
        data-testid="tenant-hint-banner"
        className="fixed top-0 inset-x-0 z-50 bg-emerald-600/90 text-white text-sm py-2 px-4 flex items-center justify-center gap-2 shadow"
      >
        <Building2 className="h-4 w-4" />
        <span>Signing into: <strong>{tenant.name}</strong></span>
      </div>
      <div className="pt-10">
        <Login tenantHint={tenant} />
      </div>
    </>
  );
}
